import React, { forwardRef } from "react";
import PropTypes from "prop-types";
import { classNames } from "../../utils/helpers";
import Icon from "../Icon";

const _Button = forwardRef((props, ref) => {
  const {
    title = "",
    className = "",
    onClick,
    disabled = false,
    loading = false,
    type = "button",
    icon = "",
    style = "",
  } = props;
  const baseClasses = `
      w-min-[200px]
      inline-flex items-center justify-center
      px-3 py-1.5
      font-semibold
      text-sm
      rounded-lg
      transition-all duration-200
      focus:outline-none focus:ring-2 focus:ring-offset-2
      h-8 min-w-[100px]
      ${
        disabled || loading ? "opacity-60 cursor-not-allowed" : "cursor-pointer"
      }
    `;

  const styleClasses = {
    primary: `
        border
        border-custom
        bg-[#009CFF] border-[#009CFF] text-white 
        hover:bg-blue-400 hover:border-blue-400
        focus:ring-blue-500
      `,
    outline: `
        border
        border-custom
        border-[#009CFF] text-[#009CFF]
        hover:bg-gray-400 hover:text-white
        focus:ring-blue-500
      `,
    secondary: `
    border-none
    bg-[#009CFF]/10
    text-[#009CFF]
    hover:bg-[#009CFF]
    hover:border-[#009CFF]
    hover:text-white
    focus:ring-[#009CFF]
  `,
    none: `
        border
        bg-white text-gray-700
        border-none
        border-gray-300
        hover:bg-gray-400 hover:text-gray-500
        focus:ring-gray-500
      `,
    gray: `
    bg-gray-100 border-0 p-1 rounded-full flex items-center justify-center text-gray-400 hover:bg-gray-400 hover:text-gray-500 transition-colors`,
    danger: `bg-red-500 border-0 text-white hover:bg-red-600 focus:ring-red-500`,
  };

  return (
    <button
      ref={ref}
      className={classNames(baseClasses, styleClasses[style] || "", className)}
      onClick={onClick}
      disabled={disabled || loading}
      type={type}
    >
      <div className="flex items-center justify-center">
        {loading && (
          <svg
            className="animate-spin h-4 w-4 text-current shrink-0"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
        )}

        {icon && !loading && (
          <Icon
            icon={icon}
            size={18}
            className={classNames("shrink-0", {
              "text-current hover:text-white": style === "outline",
              "text-current": style !== "outline",
            })}
          />
        )}
        {!loading ? (
          <div className="mx-1 text-[12px] sm:text-[12px] lg:text-sm xl:text-base text-center truncate">
            {title}
          </div>
        ) : null}
      </div>
    </button>
  );
});

_Button.displayName = "_Button";

_Button.propTypes = {
  title: PropTypes.string,
  className: PropTypes.string,
  onClick: PropTypes.func,
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  type: PropTypes.string,
  icon: PropTypes.string,
  style: PropTypes.string,
};

export default _Button;
